// biome-ignore-all lint/suspicious/noExplicitAny: internal type-erased dispatch — the post-initialize phase forwards opaque instances without inspecting their concrete shapes
/* eslint-disable @typescript-eslint/no-explicit-any */
import { EMPTY, from, lastValueFrom } from 'rxjs';
import { catchError, defaultIfEmpty, filter, mergeMap, tap } from 'rxjs/operators';
import { ModuleEventLevel } from '../../../types.js';
import { ModuleConfiguratorEventName } from '../module-configurator-event-name.js';
/**
 * Runs the post-initialize lifecycle phase for all registered modules.
 *
 * Executes two sub-steps in order:
 * 1. Calls each module's `postInitialize` hook (if defined) concurrently.
 *    Individual failures are caught and emitted as Warning events so one
 *    failing module cannot block others.
 * 2. Runs all registered `afterInit` callbacks concurrently via `Promise.allSettled`.
 *
 * @param ctx - The post-initialize phase context.
 * @param instance - The sealed module instance map produced by the initialize phase.
 * @param ref - Optional reference forwarded to each module's `postInitialize` call.
 * @returns A promise resolving when all post-initialize hooks and callbacks have settled.
 */
export async function runPostInitializePhase(ctx, instance, ref) {
    const { modules, afterInit, registerEvent } = ctx;
    registerEvent({
        level: ModuleEventLevel.Debug,
        name: ModuleConfiguratorEventName.ConfiguratorPostInitializeStart,
        message: `Post-initializing all modules [${Object.keys(instance).length}]`,
        properties: { modules: Object.keys(instance).join(', ') },
    });
    // Run postInitialize hooks for modules that declare them. Failures are caught
    // per-module via catchError so one failure does not abort the others.
    const postInitialize$ = from(modules).pipe(
    // Only process modules that define a postInitialize hook
    filter((module) => !!module.postInitialize), tap((module) => {
        registerEvent({
            level: ModuleEventLevel.Debug,
            name: ModuleConfiguratorEventName.ModulePostInitializeStart,
            message: `Module ${module.name} is being post-initialized`,
            properties: {
                moduleName: module.name,
                moduleVersion: module.version?.toString() || 'unknown',
            },
        });
    }), mergeMap((module) => {
        const postInitStart = performance.now();
        // Emit completion/error events for this module's postInitialize call as it settles
        return from(module.postInitialize({
            ref,
            modules: instance,
            instance: instance[module.name],
        })).pipe(tap(() => {
            const postInitTime = Math.round(performance.now() - postInitStart);
            registerEvent({
                level: ModuleEventLevel.Debug,
                name: ModuleConfiguratorEventName.ModulePostInitializeComplete,
                message: `Module ${module.name} has been post-initialized in ${postInitTime}ms`,
                metric: postInitTime,
                properties: {
                    moduleName: module.name,
                    moduleVersion: module.version?.toString() || 'unknown',
                    postInitTime,
                },
            });
        }), defaultIfEmpty(null), catchError((err) => {
            registerEvent({
                level: ModuleEventLevel.Warning,
                name: ModuleConfiguratorEventName.ModulePostInitializeError,
                message: `Module ${module.name} post-initialize failed`,
                properties: {
                    moduleName: module.name,
                    moduleVersion: module.version?.toString() || 'unknown',
                },
                error: err,
            });
            // Swallow module-level errors — a failing postInitialize should not
            // prevent other modules from completing their post-initialize work.
            return EMPTY;
        }));
    }), defaultIfEmpty(null));
    const postInitStart = performance.now();
    await lastValueFrom(postInitialize$);
    const postInitTime = Math.round(performance.now() - postInitStart);
    registerEvent({
        level: ModuleEventLevel.Debug,
        name: ModuleConfiguratorEventName.PostInitializeComplete,
        message: `Post-initialization of all modules completed in ${postInitTime}ms`,
        properties: { modules: Object.keys(instance).join(', '), postInitTime },
        metric: postInitTime,
    });
    // Skip the afterInit hooks entirely when none are registered
    if (!afterInit.length) {
        registerEvent({
            level: ModuleEventLevel.Debug,
            name: ModuleConfiguratorEventName.PostInitializeCompleteNoHooks,
            message: 'Post-initialization complete',
            properties: { modules: Object.keys(instance).join(', '), postInitCompleteTime: postInitTime },
        });
        return;
    }
    // Run all registered afterInit callbacks. These were added either by
    // addConfig's afterInit or via onInitialized on the configurator.
    try {
        // Build a display string of registered hook names for the start event
        // Extract each hook's name, falling back for anonymous functions
        const hookNames = afterInit.map((x) => x.name || 'anonymous').join(', ');
        registerEvent({
            level: ModuleEventLevel.Debug,
            name: ModuleConfiguratorEventName.PostInitializeHooks,
            message: `Executing post-initialize hooks [${afterInit.length}]`,
            properties: { hooks: hookNames },
        });
        const afterInitStart = performance.now();
        await Promise.allSettled(afterInit
            // Invoke every registered afterInit callback with the initialized module instance map
            .map((x) => Promise.resolve(x(instance))));
        const afterInitTime = Math.round(performance.now() - afterInitStart);
        registerEvent({
            level: ModuleEventLevel.Debug,
            name: ModuleConfiguratorEventName.PostInitializeHooksComplete,
            message: `Post-initialize hooks completed in ${afterInitTime}ms`,
            properties: {
                hooks: hookNames,
                afterInitTime,
            },
            metric: afterInitTime,
        });
    }
    catch (err) {
        // Build a display string of registered hook names for the error event
        // Extract each hook's name, falling back for anonymous functions
        const hookNames = afterInit.map((x) => x.name || 'anonymous').join(', ');
        registerEvent({
            level: ModuleEventLevel.Warning,
            name: ModuleConfiguratorEventName.PostInitializeHooksError,
            message: 'Post-initialize hooks failed',
            properties: { hooks: hookNames },
            error: err,
        });
    }
    const postInitCompleteTime = Math.round(performance.now() - postInitStart);
    registerEvent({
        level: ModuleEventLevel.Debug,
        name: ModuleConfiguratorEventName.PostInitializeComplete,
        message: 'Post-initialization complete',
        properties: { modules: Object.keys(instance).join(', '), postInitCompleteTime },
    });
}
//# sourceMappingURL=run-post-initialize-phase.js.map